import { Callout } from "@/components/ui/feedback";
import { BackupPicker, OpenCallbackChoice, useBackupTime } from "@/features/callback-backup";
import { useCallbackMinutes } from "@/features/callback-minutes";
import { CallbackPicker, useCallbackTime } from "@/features/callback-picker";
import { backupGapMinutes } from "@/lib/callbacks";
import { useAuth } from "@/lib/auth";
import { CALLBACK_MINUTES, callbackClash, whoTakesIt } from "@/lib/callbacks";
import { useAppointments, useCalendarCallbacks, useCallbackTakers } from "@/lib/queries";
import type { LeadCallback, LeadCard } from "@/lib/types";

/**
 * The one form a callback is booked with (ACL-657): who takes it, its time, its backup times, and what
 * becomes of a callback still open. The outcome window on a call, Set outcome and Book a callback all
 * show this, so a time means the same wherever it is picked. Each keeps its own state, as a window's
 * time outlives the outcome picked in it, and says what is posted.
 */
export function CallbackTimes({
  leadId, outcome, callback, backup, soon, disabled, withPurpose = false, windowMinutes, open,
}: {
  leadId: string | null;
  /** The callback outcome being booked, by slug: who takes it depends on it. */
  outcome: string | null;
  callback: ReturnType<typeof useCallbackTime>;
  backup: ReturnType<typeof useBackupTime>;
  soon: ReturnType<typeof useCallbackMinutes>;
  disabled?: boolean;
  /** Ask what the call is for: Book a callback's type says that already. */
  withPurpose?: boolean;
  windowMinutes?: number | null;
  /** The lead's callback still to come, which this one replaces. */
  open: LeadCallback | null | undefined;
}) {
  const { user } = useAuth();
  const takers = useCallbackTakers(leadId ?? "", leadId !== null && outcome !== null);
  const taker = takers.data?.find((t) => t.slug === outcome);
  const says = whoTakesIt(taker, user?.role === "rep");
  // Only a rep who would make the call has anything to clash with.
  const mine = user?.role === "rep" && taker?.booker_takes !== false;
  return (
    <div className="flex flex-col gap-3">
      {says && <Callout tone="info">{says}</Callout>}
      <CallbackPicker
        value={callback.value}
        onChange={callback.set}
        disabled={disabled}
        minutes={soon.value}
        onMinutes={soon.set}
        {...(withPurpose ? { purpose: callback.purpose, onPurposeChange: callback.setPurpose } : {})}
        windowMinutes={windowMinutes}
      />
      {mine && leadId !== null && callback.value.instant !== null && (
        <CallbackClash instant={callback.value.instant} zone={callback.value.zone} leadId={leadId} me={user.id} />
      )}
      <BackupPicker first={callback.value} backup={backup} disabled={disabled} />
      <OpenCallbackChoice open={open} callback={callback} backup={backup} gapMinutes={backupGapMinutes(windowMinutes)} />
    </div>
  );
}

/** What the rep already has then (ACL-659, ADR-217 in `platform`): it warns, and the callback saves. */
function CallbackClash({ instant, zone, leadId, me }: { instant: number; zone: string; leadId: string; me: number }) {
  const reach = CALLBACK_MINUTES * 60_000;
  const span = { start: new Date(instant - reach).toISOString(), end: new Date(instant + reach).toISOString() };
  const user_ids = String(me);
  const appointments = useAppointments({ ...span, user_ids, limit: 50 }, { retry: false });
  const callbacks = useCalendarCallbacks({ ...span, user_ids, limit: 50 }, { refetchInterval: false });
  const says = callbackClash(instant, zone, appointments.data ?? [], callbacks.data ?? [], me, leadId);
  return says ? <Callout tone="warning">{says}</Callout> : null;
}
